import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { API_ENDPOINTS } from '../../../../config/apiConfig';
import { ApprovalIntegration } from '../../../../components/generic';
import {
  renderSplitViewBadgeOrBoolean,
  splitViewFieldColSpanClass,
  splitViewFieldValueContainerClass,
  splitViewTextareaValueClass,
} from '../../../../components/generic/splitViewFieldDisplay';
import { complaintConfig } from '../../complaintConfig';
import { useFlexibleList, useEntity } from '../../../../hooks/useSWR';
import { normalizeFileName } from '../../../../components/ui/utils/fileNameEncoding';

const ComplaintDetailsTab = ({ 
  selectedRecord,
  selectedRecordFiles,
  config,
  fields,
  onRecordUpdate
}) => {
  const { theme } = useTheme();
  const formatDateDDMMYYYY = (dateValue) => {
    if (!dateValue) return '—';
    try {
      return new Date(dateValue).toLocaleDateString('en-GB');
    } catch {
      return '—';
    }
  };
  const complaintTypes = config?.lookupData?.complaintTypes?.data ||
    config?.lookupData?.complaintTypes ||
    [];
  const sectionsToUse =
    config?.getFormSectionsByType &&
    typeof config.getFormSectionsByType === 'function' &&
    selectedRecord?.complaintTypeId
      ? config.getFormSectionsByType(selectedRecord.complaintTypeId, complaintTypes)
      : config?.formSections || [];
  
  // Fetch Complaint actions taken
  const { data: complaintActionsTaken = [], isLoading: actionsTakenLoading } = useFlexibleList(
    selectedRecord?.id ? `/complaint-action-taken/complaint/${selectedRecord.id}` : null
  );
  
  // Fetch Complaint actions (corrective/preventive)
  const { data: complaintActions = [], isLoading: actionsLoading } = useFlexibleList(
    selectedRecord?.id ? `/complaint-action/complaint/${selectedRecord.id}` : null
  );
  
  // Fetch complaint details to get products for view drawer
  const { data: complaintDetails = {} } = useEntity('/complaint', selectedRecord?.id);
  const complaintProducts = Array.isArray(complaintDetails?.products)
    ? complaintDetails.products
    : [];

  return (
    <div 
      className="p-4 flex-1 overflow-y-auto advanced-table-scroll"
      style={{ 
        scrollbarWidth: 'thin',
        minHeight: 0,
        maxHeight: '100%'
      }}
    >
      <div className="grid grid-cols-1 gap-4">
        {/* Complaint Review and Approval Integration - Moved to top */}
        {complaintConfig.shouldShowApprovalInDetails() && (
          <div className="mb-6">
            <ApprovalIntegration 
              entity={selectedRecord}
              entityType="complaint"
              entityData={{
                complaintCode: selectedRecord.code,
                complaintTitle: selectedRecord.title,
                complaintType: selectedRecord.complaintType?.name,
                department: selectedRecord.department?.name,
                status: selectedRecord.status?.name,
                priority: selectedRecord.priority?.name,
                severity: selectedRecord.severity,
                reportedBy: selectedRecord.reportedBy?.userDisplayName || selectedRecord.reportedBy?.name,
                reportedAt: selectedRecord.reportedAt,
                assignedTo: selectedRecord.assignedTo?.userDisplayName || selectedRecord.assignedTo?.name,
                description: selectedRecord.description,
                source: selectedRecord.source,
                createdBy: selectedRecord.createdBy?.userDisplayName,
                createdAt: selectedRecord.createdAt
              }}
              onApprovalUpdate={(approval) => {
                console.log('Review and approval updated:', approval);
                // You can add additional logic here to refresh the complaint data
              }}
              customTitle="Complaint Review and Approval Status"
              customButtonText={complaintConfig.approval.approvalButtonText}
              showEntityDetails={complaintConfig.approval.showApprovalStatus}
              showApprovalActions={complaintConfig.approval.showApprovalActions}
              showApprovalHistory={complaintConfig.approval.showApprovalHistory}
              formMode={complaintConfig.approval.formMode || 'standalone'}
              config={{
                defaultApprovalType: complaintConfig.approval.defaultApprovalType,
                allowMultipleApprovals: complaintConfig.approval.allowMultipleApprovals
              }}
            />
          </div>
        )}
        {sectionsToUse?.map((section, idx) => {
          // Skip custom component sections - they're handled separately
          if (section.layout === 'custom' && section.customComponent) {
            if (section.title === 'Reference Document') {
              const ReferenceDocumentComponent = section.customComponent;
              return (
                <div key={idx}>
                  <ReferenceDocumentComponent
                    formData={selectedRecord}
                    config={config}
                  />
                </div>
              );
            }

            // Handle Actions Taken section
            if (section.title === 'Products') {
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-3">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>
                      {section.title} ({complaintProducts.length})
                    </div>
                  </div>
                  {complaintProducts.length > 0 ? (
                    <div className="space-y-3">
                      {complaintProducts.map((product, index) => (
                        <div key={product.id || index} className={cn(
                          "p-4 rounded-lg border transition-colors duration-200",
                          theme.border.subtle,
                          theme.colors.background,
                          "hover:border-opacity-60"
                        )}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                              <span className="font-medium">Code:</span>
                              <span>{product.productCode || '—'}</span>
                            </div>
                            <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                              <span className="font-medium">Name:</span>
                              <span>{product.productName || '—'}</span>
                            </div>
                            <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                              <span className="font-medium">Batch:</span>
                              <span>{product.batchNumber || '—'}</span>
                            </div>
                            <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                              <span className="font-medium">Quantity:</span>
                              <span>{product.productQuantity ?? '—'}</span>
                            </div>
                            <div className={cn("flex items-center gap-2 md:col-span-2", theme.text.secondary)}>
                              <span className="font-medium">Expiry:</span>
                              <span>
                                {product.expiryDate
                                  ? formatDateDDMMYYYY(product.expiryDate)
                                  : '—'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={cn("text-center py-4 text-sm", theme.text.muted)}>
                      No products recorded
                    </div>
                  )}
                </div>
              );
            }

            // Handle Actions Taken section
            if (section.title === 'Actions Taken') {
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-3">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>
                      {section.title} ({complaintActionsTaken.length})
                    </div>
                  </div>
                  {actionsTakenLoading ? (
                    <div className={cn("text-center py-4", theme.text.muted)}>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : complaintActionsTaken.length > 0 ? (
                    <div className="space-y-3">
                      {complaintActionsTaken.map((actionTaken, index) => (
                        <div key={actionTaken.id || index} className={cn(
                          "p-4 rounded-lg border transition-colors duration-200",
                          theme.border.subtle, 
                          theme.colors.background,
                          "hover:border-opacity-60"
                        )}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className={cn("text-sm font-medium mb-2", theme.text.primary)}>
                                {actionTaken.description || `Action Taken ${index + 1}`}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                  <span className="font-medium">Type:</span>
                                  <span>{actionTaken.actionTaken?.name || 'Not set'}</span>
                                </div>
                                {actionTaken.startedAt && (
                                  <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                    <span className="font-medium">Started:</span>
                                    <span>{formatDateDDMMYYYY(actionTaken.startedAt)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={cn("text-center py-4 text-sm", theme.text.muted)}>
                      No actions taken recorded
                    </div>
                  )}
                </div>
              );
            }
            
            // Handle Corrective / Preventive Actions section
            if (section.title === 'Corrective / Preventive Actions') {
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-3">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>
                      {section.title} ({complaintActions.length})
                    </div>
                  </div>
                  {actionsLoading ? (
                    <div className={cn("text-center py-4", theme.text.muted)}>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : complaintActions.length > 0 ? (
                    <div className="space-y-3">
                      {complaintActions.map((action, index) => (
                        <div key={action.id || index} className={cn(
                          "p-4 rounded-lg border transition-colors duration-200",
                          theme.border.subtle, 
                          theme.colors.background,
                          "hover:border-opacity-60"
                        )}>
                          {(() => {
                            const startedAtValue = action.startedAt || action.startDate || action.startedDate || null;
                            const dueDateValue = action.dueDate || action.dueAt || action.targetDate || null;
                            return (
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className={cn("text-sm font-medium mb-2", theme.text.primary)}>
                                {action.description || `Action ${index + 1}`}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                  <span className="font-medium">Type:</span>
                                  <span>{action.actionType?.name || 'Not set'}</span>
                                </div>
                                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                  <span className="font-medium">Started At:</span>
                                  <span>
                                    {startedAtValue ? formatDateDDMMYYYY(startedAtValue) : '—'}
                                  </span>
                                </div>
                                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                  <span className="font-medium">Due Date:</span>
                                  <span>
                                    {dueDateValue ? formatDateDDMMYYYY(dueDateValue) : '—'}
                                  </span>
                                </div>
                                {action.responsible && (
                                  <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                    <span className="font-medium">Responsible:</span>
                                    <span>{action.responsible}</span>
                                  </div>
                                )}
                                {action.closeDate && (
                                  <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                                    <span className="font-medium">Closed:</span>
                                    <span>{formatDateDDMMYYYY(action.closeDate)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {action.closeDate && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Closed
                              </span>
                            )}
                          </div>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={cn("text-center py-4 text-sm", theme.text.muted)}>
                      No corrective/preventive actions recorded
                    </div>
                  )}
                </div>
              );
            }
            
            // Handle Possible Causes section
            if (section.title === 'Possible Causes') {
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-2">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>{section.title}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {section.fields?.map((fieldName) => {
                      const f = fields?.find(ff => ff.name === fieldName);
                      if (!f || f.type === 'hidden') return null;
                      
                      let rawVal = selectedRecord[fieldName];
                      const isEmpty = rawVal == null || rawVal === "";

                      // Compute display value for select/lookups and special types
                      let displayVal = rawVal;
                      let isBoolean = false;

                      // Type-based rendering
                      if ((f.type === 'switch') || f.type === 'boolean') {
                        isBoolean = true;
                      } else if (f.type === 'date') {
                        try { 
                          if (!isEmpty) displayVal = formatDateDDMMYYYY(rawVal); 
                        } catch {}
                      }

                      return (
                        <div key={fieldName}>
                          <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                          <div className={cn("text-sm p-2 rounded flex items-center gap-2", theme.colors.background, theme.text.primary)}>
                            {isBoolean ? (
                              (rawVal === true) ? (
                                <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                                  </svg>
                                  <span>Yes</span>
                                </span>
                              ) : (rawVal === false) ? (
                                <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                                  </svg>
                                  <span>No</span>
                                </span>
                              ) : (
                                <span className={cn("inline-flex items-center gap-1", theme.text.muted)}>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <circle cx="10" cy="10" r="2"/>
                                  </svg>
                                  <span>—</span>
                                </span>
                              )
                            ) : f.type === 'textarea' ? (
                              <span className="whitespace-pre-wrap">{String(displayVal ?? '—')}</span>
                            ) : (
                              <span className={cn(isEmpty && theme.text.muted)}>{String(displayVal ?? '—')}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            
            // Handle File Attachments section
            if (section.title === 'File Attachments') {
              const activeFiles = selectedRecordFiles?.filter(file => !file.isDeleted) || [];
              if (!config?.features?.fileAttachments || !activeFiles || activeFiles.length === 0) {
                return null;
              }
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-3">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>
                      {section.title} ({activeFiles.length})
                    </div>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {activeFiles.map((file, index) => (
                      <div key={index} className={cn("flex items-center gap-3 p-3 rounded-lg border", theme.colors.background, theme.border.subtle)}>
                        <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className={cn("text-sm font-medium truncate", theme.text.primary)}>
                            {normalizeFileName(file.fileName || '')}
                          </div>
                          <div className={cn("text-xs", theme.text.secondary)}>
                            {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'} • {file.fileType || 'Unknown type'}
                          </div>
                        </div>
                        <a 
                          href={API_ENDPOINTS.fileAttachment.download(file.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn("text-xs px-3 py-1 rounded-md transition-colors", theme.colors.primary, theme.text.inverse, "hover:opacity-80")}
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            
            return null;
          }
          
          // Regular field sections
          return (
            <div key={idx}>
              <div className="flex items-center gap-2 mb-2">
                {section.icon && (
                  <span className="text-base" aria-hidden="true">{section.icon}</span>
                )}
                <div className={cn("text-sm font-semibold", theme.text.primary)}>{section.title}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.fields?.map((fieldName) => {
                  const f = fields?.find(ff => ff.name === fieldName);
                  if (!f || f.type === 'hidden') return null;
                  
                  let rawVal = selectedRecord[fieldName];
                  const isEmpty = rawVal == null || rawVal === "";

                  // Special handling for involvedDepartmentsIds (array field)
                  if (fieldName === 'involvedDepartmentsIds') {
                    const involvedDepartments = selectedRecord.involvedDepartments || [];
                    return (
                      <div key={fieldName} className="md:col-span-2">
                        <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {involvedDepartments.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {involvedDepartments.map((dept, idx) => (
                                <span key={idx} className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", theme.colors.primary, theme.text.inverse)}>
                                  {dept.name || dept}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className={cn(theme.text.muted)}>—</span>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Special handling for forwardToIds (array field)
                  if (fieldName === 'forwardToIds') {
                    const forwardTo = selectedRecord.forwardTo || [];
                    return (
                      <div key={fieldName} className="md:col-span-2">
                        <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {forwardTo.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {forwardTo.map((item, idx) => (
                                <span key={idx} className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", theme.colors.primary, theme.text.inverse)}>
                                  {item.name || item.title || item}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className={cn(theme.text.muted)}>—</span>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Compute display value for select/lookups and special types
                  let displayVal = rawVal;
                  let isBadge = false;
                  let isStatus = false;
                  let isBoolean = false;

                  // Special handling for closedById field - check closedBy directly
                  if (fieldName === 'closedById') {
                    const closedBy = selectedRecord?.closedBy;
                    
                    // If closedBy is already a string (from table transformation), use it directly
                    if (typeof closedBy === 'string' && closedBy.trim() !== '') {
                      displayVal = closedBy;
                    } else if (closedBy && typeof closedBy === 'object' && closedBy !== null && !Array.isArray(closedBy)) {
                      // If it's an object, extract the display name
                      if (closedBy.userDisplayName && closedBy.userDisplayName.trim() !== '') {
                        displayVal = closedBy.userDisplayName;
                      } else if (closedBy.userFirstName && closedBy.userLastName) {
                        const fullName = `${closedBy.userFirstName} ${closedBy.userLastName}`.trim();
                        if (fullName !== '') {
                          displayVal = fullName;
                        } else {
                          displayVal = closedBy.name || closedBy.userEmail || closedBy.email || '—';
                        }
                      } else if (closedBy.name && closedBy.name.trim() !== '') {
                        displayVal = closedBy.name;
                      } else if (closedBy.userEmail && closedBy.userEmail.trim() !== '') {
                        displayVal = closedBy.userEmail;
                      } else if (closedBy.email && closedBy.email.trim() !== '') {
                        displayVal = closedBy.email;
                      } else {
                        displayVal = '—';
                      }
                    } else {
                      displayVal = '—'; // Show dash if no user data
                    }
                  }
                  // Try table.displayField (e.g., status, department, closedBy)
                  else {
                    const displayField = f.table && f.table.displayField;
                    if (displayField && selectedRecord[displayField] != null) {
                      const displayFieldValue = selectedRecord[displayField];
                      // Handle user objects (e.g., closedBy, recordedBy, etc.)
                      if (typeof displayFieldValue === 'object' && displayFieldValue !== null && !Array.isArray(displayFieldValue)) {
                        // For user objects, try multiple property names (check userDisplayName first for users)
                        // Order matters: userDisplayName is the primary field for user display
                        if (displayFieldValue.userDisplayName) {
                          displayVal = displayFieldValue.userDisplayName;
                        } else if (displayFieldValue.userFirstName && displayFieldValue.userLastName) {
                          displayVal = `${displayFieldValue.userFirstName} ${displayFieldValue.userLastName}`.trim();
                        } else if (displayFieldValue.name) {
                          displayVal = displayFieldValue.name;
                        } else if (displayFieldValue.title) {
                          displayVal = displayFieldValue.title;
                        } else if (displayFieldValue.firstName && displayFieldValue.lastName) {
                          displayVal = `${displayFieldValue.firstName} ${displayFieldValue.lastName}`.trim();
                        } else if (displayFieldValue.userEmail) {
                          displayVal = displayFieldValue.userEmail;
                        } else if (displayFieldValue.email) {
                          displayVal = displayFieldValue.email;
                        } else {
                          displayVal = '—'; // Show dash if no user info found
                        }
                      } else {
                        displayVal = displayFieldValue;
                      }
                      isBadge = f.table && (f.table.cellRenderer === 'badge' || f.table.cellRenderer === 'statusBadge');
                      isStatus = f.table && f.table.cellRenderer === 'statusBadge';
                    } else if (/Id$/.test(f.name)) {
                      // Try related object common patterns
                      const base = f.name.replace(/Id$/, '');
                      const rel = selectedRecord[base];
                      if (rel && typeof rel === 'object' && rel !== null && !Array.isArray(rel)) {
                        // Handle user objects - check userDisplayName first
                        if (rel.userDisplayName) {
                          displayVal = rel.userDisplayName;
                        } else if (rel.userFirstName && rel.userLastName) {
                          displayVal = `${rel.userFirstName} ${rel.userLastName}`.trim();
                        } else {
                          displayVal = rel.name || 
                                     rel.title || 
                                     (rel.firstName && rel.lastName ? `${rel.firstName} ${rel.lastName}`.trim() : null) ||
                                     rel.userEmail ||
                                     rel.email ||
                                     rel.status || 
                                     rel.label || 
                                     null;
                        }
                        // If we still don't have a value, don't show the raw ID
                        if (displayVal === null || displayVal === '') {
                          displayVal = '—'; // Show dash instead of ID number or empty string
                        }
                        isBadge = true;
                      } else if (rawVal != null && rel == null) {
                        // If we have an ID but no relation object, show dash
                        displayVal = '—';
                      }
                    }
                  }

                  // Type-based rendering
                  if ((f.type === 'switch') || f.type === 'boolean') {
                    isBoolean = true;
                  } else if (f.type === 'date') {
                    try { 
                      if (!isEmpty) displayVal = formatDateDDMMYYYY(rawVal); 
                    } catch {}
                  }

                  const isLongText =
                    f.type === 'textarea' || fieldName === 'description';

                  return (
                    <div
                      key={fieldName}
                      className={splitViewFieldColSpanClass(f, fieldName, section)}
                    >
                      <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                      <div className={splitViewFieldValueContainerClass(f, fieldName, theme)}>
                        {isLongText ? (
                          <span className={splitViewTextareaValueClass()}>
                            {String(selectedRecord[fieldName] ?? '—')}
                          </span>
                        ) : (
                          renderSplitViewBadgeOrBoolean({
                            field: f,
                            record: selectedRecord,
                            theme,
                            rawVal,
                            displayVal,
                            isBadge,
                            isStatus,
                            isBoolean,
                          }) ?? (
                            <span className={cn(isEmpty && theme.text.muted)}>
                              {String(displayVal ?? '—')}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ComplaintDetailsTab;
